// External libraries
import { hasOwnProp } from '@checkout/react-components';
import parseParams from 'qs/lib/parse';

// Internal modules
import { $logger } from '@/lib/logger';
import { REDIRECT_URLS_MAP } from './constants';

/**
 * @function getParams
 * @returns {Object} Object with key/value pairs as the query string
 * parameter from the window location
 * @description Util function for making it easier to read query params
 */
const getParams = (() => {
  let params = null;

  const get = () => {
    if (!params || process.env.NODE_ENV === 'test') {
      const query = window.location.search.slice(1);

      params = parseParams(query);
    }

    return params;
  };

  get.reset = () => {
    params = null;
  };

  return get;
})();

/**
 * @function getRedirectUrl
 * @param {string} app
 * @returns {string}
 */
const getRedirectUrl = (app = 'xoon', additionalParams) => {
  let redirectUrl = REDIRECT_URLS_MAP[app];

  if (!redirectUrl) {
    $logger.warn('redirect_url_not_found', app);
    redirectUrl = REDIRECT_URLS_MAP.xoon;
  }

  return buildUrl(redirectUrl, {
    additionalParams,
    withCurrentParams: false,
  });
};

/**
 * @function buildUrl
 * @param {string} endpoint
 * @param {object} options | additionalParams, withCurrentParams
 * @returns {string}
 */
const buildUrl = (endpoint, options = {}) => {
  const { additionalParams = {}, withCurrentParams = true } = options;
  const baseParams = withCurrentParams ? getParams() : {};
  const params = Object.assign({}, baseParams, additionalParams);

  // `cmd` is a reserved slingshot param that we need to
  // remove for client redirects.
  if (hasOwnProp(params, 'cmd')) {
    delete params.cmd;
  }

  const separator = endpoint.includes('?') ? '&' : '?';
  const url = Object.entries(params).reduce((url, [key, value], i) => {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(value);
    return `${url}${encodedKey}=${encodedValue}&`;
  }, `${endpoint}${separator}`);

  return url.replace(/[?&]$/, '');
};

export { buildUrl, getParams, getRedirectUrl };
